// ═══════════════════════════════════════════════════════════════════════════
//  ΠΛΗΡΩΜΕΣ & ΕΓΓΥΗΣΗ — Η ΜΙΑ πηγή του frontend.
//  ---------------------------------------------------------------------------
//  Καμία οθόνη δεν καλεί μόνη της endpoint πληρωμών. Το βήμα «Πληρωμές» του
//  Agreement, η σελίδα /pay/{token} και το tab της καρτέλας ναύλου περνούν
//  ΟΛΕΣ από εδώ — ίδια δεδομένα, ίδιοι υπολογισμοί, καμία διπλή λογική.
//
//  Ποσά: ο server μιλάει σε ΛΕΠΤΑ (amount_cents). Η μετατροπή σε «1.234,56 €»
//  γίνεται με το κοινό formatMoneyEU (utils/format).
// ═══════════════════════════════════════════════════════════════════════════
/* Το ΙΔΙΟ αρχείο τρέχει και στο v2 (/api-v2) και στο prod (/api) — η σελίδα
   /pay/{token} σερβίρεται και από τα δύο. Το base βγαίνει από τη διαδρομή, ώστε
   να μη χρειάζεται δεύτερο αντίγραφο του service. */
export const PAY_API = (typeof window !== 'undefined' && window.location.pathname.startsWith('/v2/'))
  ? '/api-v2' : (typeof window !== 'undefined' && window.location.port === '8443' ? '/api-v2' : '/api');
const API = PAY_API;

/* `charter_pack` = το πακέτο του σκάφους (πανιά/σεντόνια/καθαριότητα…), τιμή
   και περιεχόμενο από το vessel_owners.custom_fields — ΟΧΙ extra του καταλόγου. */
export type PayPurpose = 'balance' | 'deposit' | 'damage_waiver' | 'extra' | 'charter_pack';

export type PayRow = {
  id: number;
  purpose: PayPurpose;
  extra_code: string | null;
  description: string;
  amount_cents: number;
  currency: string;
  kind: 'charge' | 'preauth';
  status: 'pending' | 'authorised' | 'captured' | 'paid' | 'released' | 'refunded' | 'failed' | 'cancelled';
  captured_cents: number;
  refunded_cents: number;
  provider: string;
  receipt_no: string | null;
  expires_at: string | null;
  created_at: string;
  authorised_at: string | null;
  captured_at: string | null;
  released_at: string | null;
  provider_ref?: string | null;
  note?: string | null;
};

export type PayExtra = {
  code: string; name: string;
  /** ΤΟ ΧΡΕΩΣΙΜΟ ΠΟΣΟ για αυτόν τον ναύλο: unit_price_cents × qty. */
  price_cents: number;
  /** 'charter' | 'day' | 'week' | 'person' — βλ. utils/charterExtras. */
  unit: string;
  /** Η τιμή ΑΝΑ ΜΟΝΑΔΑ, όπως είναι στον κατάλογο. */
  unit_price_cents?: number;
  /** Πόσες μονάδες χρεώνεται ο ναύλος (ημέρες/εβδομάδες/άτομα). */
  qty?: number;
  /** Ετικέτα μονάδας για την οθόνη — π.χ. «ημέρες». */
  unit_label?: string;
  vat_pct: string | number; in_charter: boolean; paid: boolean;
  /** true = ο πελάτης μπορεί να το αγοράσει μόνος του από τη σελίδα πληρωμών. */
  online?: boolean;
  /** «Τι περιλαμβάνει» — ορίζεται στη φόρμα σκάφους, το βλέπει και ο πελάτης. */
  includes?: string[];
};

/** Τα δύο εργαλεία του Virtual POS Πειραιώς — έρχονται από τον server. */
export type PosUrls = { charge_url: string; admin_url: string };

export type PayProvider = {
  provider: string; label: string; mode: string; supports: string[];
  pos?: PosUrls; bank_details?: string;
};

/* ═══ ΔΟΣΕΙΣ ΝΑΥΛΟΥ ════════════════════════════════════════════════════════
   Μία μορφή για ΟΛΟ το σύστημα (server → οθόνη → email → DOCX):
     pct       ποσοστό επί του συνόλου του ναύλου
     due_days  πόσες ημέρες
     due_from  'confirmation' = ΜΕΤΑ την επιβεβαίωση · 'checkin' = ΠΡΙΝ τον ναύλο
     due_date  η υπολογισμένη ημερομηνία (ή χειροκίνητη, όταν τη γράψει ο χρήστης)
   Το `amount_cents` βγαίνει από pct × σύνολο· γράφεται μόνο όταν ο υπάλληλος
   πληκτρολογήσει ποσό με το χέρι, οπότε και κλειδώνει. */
export type Instalment = {
  key: string; label: string; label_en?: string; pct: number;
  amount_cents: number; due_days?: number; due_from?: 'confirmation' | 'checkin';
  due_date?: string; overdue?: boolean; paid?: boolean;
};

export type InstalmentPlan = {
  /** 'booking' = ο ναύλος έχει δικό του πρόγραμμα · 'default' = της βάσης. */
  source: 'booking' | 'default';
  confirmed_date: string;
  checkin_date: string;
  rows: Instalment[];
};

export type PayState = {
  enabled: boolean;
  providers: PayProvider[];
  booking_number: string;
  currency: string;
  total_cents: number;
  paid_cents: number;
  /** Χειρόγραφες καταχωρήσεις καρτέλας (booking_data.payments). */
  manual_paid_cents: number;
  /** Ό,τι πέρασε από charter_payments (link, κάρτα επιτόπου, manual record). */
  online_paid_cents: number;
  balance_cents: number;
  deposit_cents: number;
  /** 'charter' = ποσό του ναύλου · 'vessel' = εγγύηση του σκάφους · '' = κανένα */
  deposit_source: 'charter' | 'vessel' | '';
  waiver_cents: number;
  waiver_source: 'charter' | 'vessel' | 'rule' | '';
  /** Charter pack — τιμή του ΣΚΑΦΟΥΣ (custom_fields.charter_pack). */
  pack_cents: number;
  pack_source: 'charter' | 'vessel' | '';
  /** «Τι περιλαμβάνει» το pack — custom_fields.charter_pack_includes. */
  pack_includes: string[];
  /** >0 → έχει ήδη πληρωθεί· δεν ξαναμπαίνει σε σύνδεσμο. */
  pack_paid_cents: number;
  /** Οι δόσεις του ναύλου με ποσά, προθεσμία και `paid` — η ΜΙΑ πηγή για το παράθυρο συνδέσμου. */
  instalments: Instalment[];
  /** Ο ΚΑΝΟΝΑΣ που επεξεργάζεται ο υπάλληλος (ποσοστά, ημέρες, από πού μετρούν). */
  instalment_plan?: InstalmentPlan;
  /** Ημερομηνία επιβίβασης — αφετηρία των «πριν τον ναύλο» δόσεων. */
  checkin_date?: string;
  /** Ημερομηνία επιβεβαίωσης — αφετηρία των «μετά την επιβεβαίωση» δόσεων. */
  confirmed_date?: string;
  /** Όροι πληρωμής & ακύρωσης της βάσης του ναύλου. Μία πηγή για email/pay/Agreement/DOCX. */
  terms?: { en: string; el: string };
  deposit_mode: 'preauth' | 'charge';
  deposit_row: { id: number; purpose: PayPurpose; status: string; amount_cents: number; expires_at: string | null } | null;
  /** Ό,τι μπορεί να αγοράσει ΜΟΝΟΣ ΤΟΥ ο πελάτης (online = true). */
  extras: PayExtra[];
  /** Ο ΠΛΗΡΗΣ κατάλογος — μόνο για υπάλληλο (παράθυρο συνδέσμου πληρωμής). */
  extras_all: PayExtra[];
  rows: PayRow[];
  can_manage: boolean;
  /** Μόνο για υπάλληλο· ο πελάτης παίρνει null. */
  pos: PosUrls | null;
  /** Παραλήπτες με email (ναυλωτής/σκίπερ/broker/πλήρωμα) — μόνο για υπάλληλο. */
  recipients: Array<{ id: string; label: string; email: string; role: string; phone: string }>;
};

export type CheckoutResult = {
  payment_id: number;
  provider: string;
  kind: 'charge' | 'preauth';
  amount_cents: number;
  redirect_url: string;
  open_pos: boolean;
  pos: PosUrls;
  instructions: string;
  items: Array<{ code: string; name: string; amount_cents: number }>;
};

async function call(url: string, init?: RequestInit): Promise<any> {
  const res = await fetch(url, init);
  let body: any = null;
  try { body = await res.json(); } catch { /* κενό σώμα */ }
  if (!res.ok || !body?.success) {
    const err: any = new Error(body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = body?.data ?? null;
    throw err;
  }
  return body.data;
}

const post = (url: string, body: any) =>
  call(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

/* ── Ενεργοί πάροχοι (χωρίς ναύλο). Χωρίς πάροχο → το βήμα κρύβεται. ── */
export async function getProviders(bookingNumber?: string): Promise<{ enabled: boolean; providers: PayProvider[] }> {
  // Ο ναύλος στέλνεται ΠΑΝΤΑ όταν τον ξέρουμε: ο συνδεδεμένος πελάτης δεν
  // περνάει το yms_guard_booking χωρίς ναύλο στο αίτημα (403 booking_scope_required).
  const q = bookingNumber ? `&booking_number=${encodeURIComponent(bookingNumber)}` : '';
  return call(`${API}/payments.php?action=providers${q}`);
}

/* ── Πλήρης κατάσταση πληρωμών ενός ναύλου. ── */
export async function getPayments(bookingNumber: string): Promise<PayState> {
  return call(`${API}/payments.php?booking_number=${encodeURIComponent(bookingNumber)}`);
}

/* ── Ο πελάτης ξεκινά πληρωμή. Το ΠΟΣΟ το ορίζει ο server. ── */
export async function checkout(opts: {
  bookingNumber: string; purpose: PayPurpose;
  amountCents?: number; extraCodes?: string[]; language?: string; email?: string;
  /** Ρητός πάροχος (ο πελάτης/υπάλληλος πάτησε «κάρτα» ή «έμβασμα»).
      Κενό = ο πρώτος ενεργός που μπορεί να το κάνει. */
  provider?: string;
}): Promise<CheckoutResult> {
  return post(`${API}/payments.php`, {
    action: 'checkout',
    booking_number: opts.bookingNumber,
    purpose: opts.purpose,
    amount_cents: opts.amountCents ?? 0,
    extra_codes: opts.extraCodes ?? [],
    language: opts.language ?? 'en',
    email: opts.email ?? '',
    provider: opts.provider ?? '',
    /* Πού να γυρίσει ο πάροχος: στη σελίδα από την οποία ξεκίνησε η πληρωμή.
       Ο server δέχεται ΜΟΝΟ δικό μας domain (yms_pay_safe_url). */
    return_url: typeof window !== 'undefined' ? window.location.href : '',
  });
}

/* ── ΕΙΣΠΡΑΞΗ ΕΚΤΟΣ ΣΥΣΤΗΜΑΤΟΣ (μετρητά / έμβασμα / POS βάσης) ───────────────
   Ο υπάλληλος ΚΑΤΑΓΡΑΦΕΙ ό,τι ήδη εισέπραξε. Η κίνηση μπαίνει 'paid', παίρνει
   αριθμό απόδειξης και ειδοποιεί το γραφείο — ίδια μεταχείριση με κάθε άλλη. */
export const recordManual = (opts: {
  bookingNumber: string; purpose: PayPurpose; amount: string | number;
  method: 'cash' | 'transfer' | 'pos' | 'other'; note?: string; extraCode?: string;
}) => post(`${API}/payments.php`, {
  action: 'record_manual', booking_number: opts.bookingNumber, purpose: opts.purpose,
  amount: opts.amount, method: opts.method, note: opts.note ?? '', extra_code: opts.extraCode ?? null,
});

/* ── Υπάλληλος: σύνδεσμος πληρωμής /pay/{token} ──────────────────────────────
   Πολλά σκέλη σε ΕΝΑ link (π.χ. εγγύηση + extras). Τα ποσά έρχονται ήδη
   προσυμπληρωμένα από την οθόνη· ο υπάλληλος μπορεί να τα έχει αλλάξει.
   ΔΕΝ χρειάζεται ενεργός πάροχος για να φτιαχτεί σύνδεσμος. */
export type NewLinkItem = {
  purpose: PayPurpose; code?: string | null; name?: string;
  amount_cents: number; optional?: boolean;
};

/** Δόση όπως γυρίζει το create_link — ΜΕ το ποσό της, όχι μόνο ποσοστό. */
export type LinkInstalmentOut = { key: string; label: string; label_en?: string; pct: number;
                                  amount_cents: number; due_date?: string; overdue?: boolean };

export type CreatedLink = {
  token: string; url: string; expires_at: string;
  amount_cents: number; purpose: string; items: Required<NewLinkItem>[];
  instalments?: LinkInstalmentOut[];
  /** Το σύνολο του ναύλου τη στιγμή της δημιουργίας — για το email. */
  charter_total_cents?: number;
  /* ΟΛΟ το πρόγραμμα δόσεων με κατάσταση ανά γραμμή — για τον πίνακα του email
     και της σελίδας. Κενό όταν ο σύνδεσμος δεν αφορά τον ναύλο. */
  instalment_plan?: Array<Instalment & { status: 'paid' | 'now' | 'pending' }>;
  /** Οι όροι πληρωμής & ακύρωσης της βάσης — μπαίνουν ολόκληροι στο email. */
  terms?: { en: string; el: string };
};

export async function createLink(opts: {
  bookingNumber: string; items?: NewLinkItem[];
  purpose?: PayPurpose; amountCents?: number; language?: string; note?: string;
  /* ΟΙ ΔΟΣΕΙΣ ΠΟΥ ΔΙΑΛΕΞΕ Ο ΥΠΑΛΛΗΛΟΣ — ΛΙΣΤΑ, ΟΧΙ ΝΑΙ/ΟΧΙ.
     Ο σύνδεσμος δείχνει ΜΟΝΟ αυτές. Με `true` (παλιά μορφή) ο server πέφτει
     στο YMS_INSTALMENTS_DEFAULT και στέλνει και τις τρεις — αυτό ακριβώς ήταν
     το λάθος: ο υπάλληλος διάλεγε «δόσεις» και έφευγαν όλες. */
  instalments?: boolean | Array<{ key: string; label: string; pct: number; due_date?: string; label_en?: string }>;
}): Promise<CreatedLink> {
  return post(`${API}/payments.php`, {
    action: 'create_link', booking_number: opts.bookingNumber,
    items: opts.items ?? [], purpose: opts.purpose ?? 'balance',
    amount_cents: opts.amountCents ?? 0, language: opts.language ?? 'en',
    note: opts.note ?? '',
    instalments: Array.isArray(opts.instalments) ? opts.instalments : !!opts.instalments,
  });
}

/** Ίχνος αποστολής — ποιοι έλαβαν τον σύνδεσμο και πότε. */
export const markLinkSent = (bookingNumber: string, token: string, sentTo: string) =>
  post(`${API}/payments.php`, { action: 'mark_sent', booking_number: bookingNumber, token, sent_to: sentTo });

/** Οι σύνδεσμοι του ναύλου (κατάσταση/λήξη/παραλήπτες). */
export const cancelLink = (bookingNumber: string, token: string) =>
  post(`${API}/payments.php`, { action: 'cancel_link', booking_number: bookingNumber, token });

export const getLinks = (bookingNumber: string) =>
  call(`${API}/payments.php?action=links&booking_number=${encodeURIComponent(bookingNumber)}`);

/* ── Υπάλληλος: εγγύηση ── */
export const capture = (bookingNumber: string, paymentId: number, amountCents?: number) =>
  post(`${API}/payments.php`, { action: 'capture', booking_number: bookingNumber, payment_id: paymentId, amount_cents: amountCents ?? 0 });

export const release = (bookingNumber: string, paymentId: number) =>
  post(`${API}/payments.php`, { action: 'release', booking_number: bookingNumber, payment_id: paymentId });

export const refund = (bookingNumber: string, paymentId: number, amountCents?: number) =>
  post(`${API}/payments.php`, { action: 'refund', booking_number: bookingNumber, payment_id: paymentId, amount_cents: amountCents ?? 0 });

export const markPaid = (bookingNumber: string, paymentId: number, note?: string) =>
  post(`${API}/payments.php`, { action: 'mark_paid', booking_number: bookingNumber, payment_id: paymentId, note: note ?? '' });

export const syncPayment = (bookingNumber: string, paymentId: number) =>
  post(`${API}/payments.php`, { action: 'sync', booking_number: bookingNumber, payment_id: paymentId });

/* ── Δημόσιος σύνδεσμος /pay/{token} ── */
export const getLink = (token: string) =>
  call(`${API}/pay-link.php?token=${encodeURIComponent(token)}`);

/** «Πίσω» πριν την πληρωμή: ακυρώνει την απόπειρα και ξαναανοίγει τον σύνδεσμο.
    Ο server αρνείται αν κάτι έχει ήδη εισπραχθεί ή δεσμευτεί. */
export const cancelLinkAttempt = (token: string) =>
  post(`${API}/pay-link.php`, { token, action: 'cancel' });

/** Ένα ΜΠΛΟΚ της σελίδας πληρωμής — κάθε ένα πληρώνεται χωριστά. */
export type PayBlock = 'instalment' | 'extra' | 'charter_pack' | 'deposit' | 'damage_waiver'
                     | 'balance' | 'custom';

export const startLink = (token: string, email?: string, picked?: string[], provider?: string,
                          instalment?: string, block?: PayBlock | '') =>
  post(`${API}/pay-link.php`, { token, email: email ?? '', picked: picked ?? [],
                                provider: provider ?? '', instalment: instalment ?? '',
                                /* ΠΟΙΟ μπλοκ πληρώνεται. Κενό = όλα μαζί (παλιά σελίδα). */
                                block: block ?? '' });

/* ── Καθολική εικόνα (Λογιστικά → Πληρωμές online) ── */
export type PayOverview = {
  enabled: boolean;
  providers: PayProvider[];
  tiles: {
    settled_cents: number; pending_cents: number; held_cents: number; refunded_cents: number;
    n_all: number; n_pending: number; n_failed: number; n_bookings: number;
  };
  by_purpose: Array<{ purpose: PayPurpose; n: number; settled_cents: number }>;
  by_provider: Array<{ provider: string; n: number; settled_cents: number }>;
  open_links: { n: number; amount_cents: number };
  pos: PosUrls | null;
  rows: Array<PayRow & { booking_number: string; booking_norm: string; paid_by_email: string | null; created_by: string }>;
  truncated: boolean;
};

export function getOverview(f: {
  from?: string; to?: string; status?: string; provider?: string; purpose?: string; q?: string;
} = {}): Promise<PayOverview> {
  const p = new URLSearchParams({ action: 'overview' });
  Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
  return call(`${API}/payments.php?${p.toString()}`);
}

/* ── Ρυθμίσεις (ADMIN/FINANCIAL) ── */
export const getPaymentConfig = () => call(`${API}/payment-providers.php`);
export const saveProvider = (body: any) => post(`${API}/payment-providers.php`, { action: 'save_provider', ...body });
/** Έλεγχος σύνδεσης με τα ΑΠΟΘΗΚΕΥΜΕΝΑ κλειδιά — αβλαβής κλήση, τίποτα δεν χρεώνεται. */
export const testProviderConnection = (provider: string, base_id: number | null) =>
  post(`${API}/payment-providers.php`, { action: 'test_connection', provider, base_id });
export const savePaymentSettings = (body: any) => post(`${API}/payment-providers.php`, { action: 'save_settings', ...body });

/* ── ΔΟΣΕΙΣ & ΟΡΟΙ ΑΝΑ ΒΑΣΗ (Διαχείριση App → Πληρωμές) ──────────────────── */
export const saveBaseSettings = (body: {
  base_id: number | null;
  instalments?: Instalment[];
  terms_en?: string; terms_el?: string;
  /** true → η βάση ξαναπέφτει στην προεπιλογή (σβήνει τη γραμμή της). */
  use_default?: boolean;
}) => post(`${API}/payment-providers.php`, { action: 'save_base_settings', ...body });

/* ── ΔΟΣΕΙΣ ΕΝΟΣ ΝΑΥΛΟΥ (καρτέλα → Πληρωμές & Εγγύηση) ───────────────────
   Ο ναύλος αποκτά ΔΙΚΟ ΤΟΥ πρόγραμμα μόλις σωθεί εδώ — καμία μελλοντική
   αλλαγή του default δεν τον ξαναγγίζει. Η «Επαναφορά» σβήνει το δικό του
   και τον ξαναρίχνει στο default της βάσης. */
export const saveInstalments = (bookingNumber: string, instalments: Instalment[]) =>
  post(`${API}/payments.php`, { action: 'save_instalments', booking_number: bookingNumber, instalments });

export const resetInstalments = (bookingNumber: string) =>
  post(`${API}/payments.php`, { action: 'reset_instalments', booking_number: bookingNumber });
/* Ο ΚΑΤΑΛΟΓΟΣ EXTRAS ΔΕΝ ΕΙΝΑΙ ΕΔΩ (31Aug2026): ζει στο services/extrasService
   (api/extras.php), γιατί τον διαβάζει και η φόρμα ναύλου — όχι μόνο τα
   Λογιστικά. Μία πηγή για τιμές, μονάδες και «τι περιλαμβάνει». */
